import { generateId } from './ids';
import type {
  Checkin,
  CurvePoint,
  Rehearsal,
  RehearsalDraft,
  Workspace,
  WorkspaceConsent,
  WorkspaceId,
} from './types';
import { computeCurve } from './curve';

const LS_WORKSPACE = 'cr:workspace';
const LS_REHEARSALS = 'cr:rehearsals';
const LS_DRAFT = 'cr:draft';
const LS_CONSENT = 'cr:consent'; // local mirror

export type PersistedRehearsal = Rehearsal;

let memory: {
  workspace: Workspace | null;
  rehearsals: Rehearsal[];
} = { workspace: null, rehearsals: [] };

function load(): void {
  try {
    const wsRaw = localStorage.getItem(LS_WORKSPACE);
    if (wsRaw) memory.workspace = JSON.parse(wsRaw);

    const rRaw = localStorage.getItem(LS_REHEARSALS);
    if (rRaw) memory.rehearsals = JSON.parse(rRaw);
  } catch {
    // ignore corrupted
  }
}

function persist(): void {
  try {
    if (memory.workspace) localStorage.setItem(LS_WORKSPACE, JSON.stringify(memory.workspace));
    localStorage.setItem(LS_REHEARSALS, JSON.stringify(memory.rehearsals));
  } catch {
    // quota etc — still usable in memory this session
  }
}

export function getWorkspace(): Workspace {
  load();
  if (!memory.workspace) {
    const id = generateId() as WorkspaceId;
    const now = Date.now();
    const consent: WorkspaceConsent = {
      consent_version: '2025-06',
      retention_days: 365,
      allow_server_storage: false,
      allow_analytics: false,
      allow_followups: false,
    };
    memory.workspace = { id, created_at: now, consent };
    persist();
  }
  return memory.workspace;
}

export function updateConsent(partial: Partial<WorkspaceConsent>): Workspace {
  const ws = getWorkspace();
  const updated: Workspace = {
    ...ws,
    consent: { ...ws.consent, ...partial },
  };
  if (partial.allow_server_storage || partial.allow_analytics || partial.allow_followups) {
    updated.consent.consent_granted_at = Date.now();
  }
  memory.workspace = updated;
  persist();
  return updated;
}

export function getRehearsals(): Rehearsal[] {
  load();
  return [...memory.rehearsals].sort((a, b) => b.created_at - a.created_at);
}

export function getCurvePoints(): CurvePoint[] {
  return computeCurve(getRehearsals());
}

export function getDraft(): RehearsalDraft {
  try {
    const raw = localStorage.getItem(LS_DRAFT);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveDraft(draft: RehearsalDraft): void {
  try {
    localStorage.setItem(LS_DRAFT, JSON.stringify(draft));
  } catch {}
}

export function clearDraft(): void {
  localStorage.removeItem(LS_DRAFT);
}

export function saveRehearsalLocally(rehearsal: Omit<Rehearsal, 'workspace_id' | 'id' | 'created_at'> & Partial<Pick<Rehearsal, 'id' | 'created_at'>>): Rehearsal {
  const ws = getWorkspace();
  const full: Rehearsal = {
    id: rehearsal.id || generateId(),
    workspace_id: ws.id,
    created_at: rehearsal.created_at || Date.now(),
    ...rehearsal,
  } as Rehearsal;

  load();
  // de-dupe if re-saving
  memory.rehearsals = memory.rehearsals.filter((r) => r.id !== full.id);
  memory.rehearsals.push(full);
  persist();
  return full;
}

export function addCheckinLocally(checkin: Omit<Checkin, 'id' | 'workspace_id'>): Checkin {
  const ws = getWorkspace();
  const full: Checkin = {
    id: generateId(),
    workspace_id: ws.id,
    ...checkin,
  };

  // attach to the parent rehearsal for UI
  load();
  const r = memory.rehearsals.find((x) => x.id === checkin.rehearsal_id);
  if (r) {
    r.checkins = [...(r.checkins || []), full];
  }
  persist();
  return full;
}

export function completeCheckin(checkinId: string, updates: Partial<Checkin>): void {
  load();
  for (const r of memory.rehearsals) {
    if (!r.checkins) continue;
    const idx = r.checkins.findIndex((c) => c.id === checkinId);
    if (idx >= 0) {
      r.checkins[idx] = { ...r.checkins[idx], ...updates, completed_at: Date.now() };
      break;
    }
  }
  persist();
}

export function deleteRehearsal(id: string): void {
  load();
  memory.rehearsals = memory.rehearsals.filter((r) => r.id !== id);
  persist();
}

export function deleteAllData(): void {
  memory.workspace = null;
  memory.rehearsals = [];
  localStorage.removeItem(LS_WORKSPACE);
  localStorage.removeItem(LS_REHEARSALS);
  localStorage.removeItem(LS_DRAFT);
  localStorage.removeItem(LS_CONSENT);
}

// Export for user sovereignty
export function exportAll(): string {
  const payload = {
    exported_at: new Date().toISOString(),
    workspace: getWorkspace(),
    rehearsals: getRehearsals(),
  };
  return JSON.stringify(payload, null, 2);
}

// Very light "server sync" simulation layer.
// In production the real calls go to the Worker.
// When allow_server_storage is true we attempt fetch; on failure we stay local.
const API_BASE = '/api';

export async function syncRehearsalToServer(rehearsal: Rehearsal, consent: WorkspaceConsent): Promise<boolean> {
  if (!consent.allow_server_storage) return false;
  try {
    const res = await fetch(`${API_BASE}/rehearsals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': rehearsal.workspace_id,
      },
      body: JSON.stringify(rehearsal),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchServerRehearsals(workspaceId: WorkspaceId, consent: WorkspaceConsent): Promise<Rehearsal[] | null> {
  if (!consent.allow_server_storage) return null;
  try {
    const res = await fetch(`${API_BASE}/rehearsals`, {
      headers: { 'X-Workspace-Id': workspaceId },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function createServerCheckin(checkin: Checkin, workspaceId: WorkspaceId): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/checkins`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspaceId,
      },
      body: JSON.stringify(checkin),
    });
    return res.ok;
  } catch {
    return false;
  }
}
